import { useState, useRef, useEffect, useCallback } from "react";

// Manages a single object URL for a preview: revokes the previous URL whenever
// it is replaced, cleared, or the component unmounts — preventing the blob
// memory leak that built up across repeated photo captures/retakes.
// setRaw() is for remote URLs (e.g. wardrobe images) which must NOT be revoked.
export function useObjectUrl() {
  const [url, setUrl] = useState(null);
  const owned = useRef(false);
  const cur = useRef(null);

  const revoke = useCallback(() => {
    if (owned.current && cur.current) URL.revokeObjectURL(cur.current);
  }, []);

  const setFromFile = useCallback((file) => {
    revoke();
    const u = file ? URL.createObjectURL(file) : null;
    owned.current = !!u;
    cur.current = u;
    setUrl(u);
  }, [revoke]);

  const setRaw = useCallback((u) => {
    revoke();
    owned.current = false;
    cur.current = u || null;
    setUrl(u || null);
  }, [revoke]);

  const clear = useCallback(() => {
    revoke();
    owned.current = false;
    cur.current = null;
    setUrl(null);
  }, [revoke]);

  useEffect(() => () => revoke(), [revoke]);

  return { url, setFromFile, setRaw, clear };
}
